import { useNavigate, useParams } from "react-router-dom";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import "./CustomerV2Preview.css";
import {
  checkCustomerExist,
  fetchB2BCustomerByPhoneNumber,
  fetchCoupounDetails,
  fetchCustomerLoyaltyPointDetails,
  fetchRetailCustomer,
  getAllOrders,
  getCustomerLoyaltyDetail,
  getCustomerOrdersV1,
  getCustomerOrderSummary,
  getRetailUserDetails,
  getWalletBalance,
  getLoyaltyDetails,
  getCustomerDetailsV2,
  customerAnalyticsOverviewApi,
} from "../../../../../config/Services";
import { useEffect, useState } from "react";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { ChevronLeftIcon, EnvelopeIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useSnackbar } from "../../hooks/SnackbarProvider";
import CustomerPaginatedTable from "./Component/CustomComponent/CustomerPaginatedTable";
import WalletTab from "./Component/WalletTabComponent/WalletTab";
import { formatCurrency, formatDate, getInitials } from "./Component/helper/helper";
import LoyaltyTab from "./Component/LoyaltyTab/LoyaltyTab";
import OverviewTab from "./Component/OverviewTab/OverviewTab";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Shared atoms ─────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub }) => (
  <div className="cpv__stat-card">
    <span className="cpv__stat-label">{label}</span>
    <span className="cpv__stat-value">{value || "N/A"}</span>
    {sub && <span className="cpv__stat-sub">{sub}</span>}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="cpv__info-row">
    <span className="cpv__info-label">{label}</span>
    <span className="cpv__info-value">{value || "N/A"}</span>
  </div>
);

const AddressAccordion = ({ title, address }) => {
  const [open, setOpen] = useState(title === "Default Address");
  return (
    <div className="cpv__accordion">
      <button className="cpv__accordion-header" onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        <span className={`cpv__accordion-chevron ${open ? "open" : ""}`}>›</span>
      </button>
      {open && (
        <div className="cpv__accordion-body">
          {address ? (
            <>
              <p>{address?.line1 || "N/A"}</p>
              <p>{[address?.city, address?.state]?.filter(Boolean).join(", ") || "N/A"}</p>
              <p>{address?.country || "N/A"}</p>
            </>
          ) : (
            <p>N/A</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Order columns ────────────────────────────────────────────────────────────

const ORDER_COLUMNS = [
  {
    key: "orderId",
    label: "Order ID",
    width: "25%",
    render: (row) => (
      <div className="cpv__order-id-cell">
        <span className="cpv__order-id">{row?.orderId}</span>
        <span className="cpv__order-date">{row?.orderDate}</span>
      </div>
    ),
  },
  { key: "channel", label: "Channel", width: "35%" },
  { key: "amount", label: "Amount", width: "15%" },
  {
    key: "status",
    label: "Status",
    width: "25%",
    render: (row) => (
      <span className={`cpv__order-badge cpv__order-badge--${row?.status?.toLowerCase()}`}>
        {row?.status}
      </span>
    ),
  },
];

const mapOrderRows = (orders) =>
  (orders ?? [])?.map((o) => ({
    orderId: o?.baseOrderResponse?.orderId ?? "N/A",
    orderDate: formatDate(o?.baseOrderResponse?.createdAt ?? o?.orderDate),
    channel: o?.baseOrderResponse?.salesChannel ?? o?.orderType ?? "N/A",
    amount: `₹ ${o?.baseOrderResponse?.amountPaid ?? o?.orderBillingDetails?.grandTotal ?? "N/A"}`,
    status: o?.baseOrderResponse?.paymentStatus ?? "N/A",
  }));

// ─── Coupon columns ───────────────────────────────────────────────────────────

const COUPON_COLUMNS = [
  {
    key: "code",
    label: "Coupon Code",
    width: "15%",
    render: (row) => (
      <div className="cpv__order-id-cell">
        <span className="cpv__coupon-code">{row?.code}</span>
        {/* <span className="cpv__order-date">{row.couponId}</span> */}
      </div>
    ),
  },
  { key: "offer", label: "Offer Name", width: "38%" },
  { key: "discount", label: "Discount", width: "15%" },
  { key: "validTo", label: "Valid Till", width: "20%" },
  {
    key: "status",
    label: "Status",
    width: "12%",
    render: (row) => (
      <span
        className={`cpv__coupon-status ${
          row?.status?.toLowerCase() === "active" ? "active" : "used"
        }`}
      >
        {row.status}
      </span>
    ),
  },
];

const mapCouponRows = (coupons) =>
  (coupons ?? []).map((c) => ({
    code: c?.couponCode ?? "N/A",
    couponId: c?.couponId ?? "",
    offer: c?.offerName ?? c?.description ?? "N/A",
    discount:
      c?.discountType === "FLAT_OFF"
        ? `₹ ${c?.discountValue ?? "N/A"} off`
        : c?.discountType === "PERCENTAGE_OFF"
        ? `${c?.discountValue ?? "N/A"}% off`
        : c?.discountValue != null
        ? c.discountValue
        : "N/A",
    validTo: c?.validTo ? formatDate(c?.validTo) : "N/A",
    status: c?.status ?? "N/A",
  }));

// ─── Constants ────────────────────────────────────────────────────────────────

const DETAIL_TABS = ["Overview", "Order", "Loyalty", "Wallet", "Coupons"];

// ─── Main ─────────────────────────────────────────────────────────────────────

const CustomerV2Preview = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");

  const [loading, setLoading] = useState(true);
  const [activeDetailTab, setActiveDetailTab] = useState("Overview");
  const [mobileNumber, setMobileNumber] = useState("");

  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [customerExists, setCustomerExists] = useState(true);

  const [tabLoading, setTabLoading] = useState(false);

  const [tabOrders, setTabOrders] = useState([]);
  const [tabOrdersMeta, setTabOrdersMeta] = useState({
    page: 0,
    pageSize: 10,
    totalResults: 0,
    totalPages: 0,
  });

  const [tabLoyalty, setTabLoyalty] = useState(null);
  const [customerUidx, setCustomerUidx] = useState(null);
  const [loyaltyDetails, setLoyaltyDetails] = useState(null);
  const [tabWallet, setTabWallet] = useState(null);

  const [tabCoupons, setTabCoupons] = useState([]);
  const [tabCouponsMeta, setTabCouponsMeta] = useState({
    page: 0,
    pageSize: 10,
    totalResults: 0,
    totalPages: 0,
  });

  useEffect(() => {
    setLoading(true);
    if (params?.customerType === "b2b") {
      fetchB2bCustomersEndpoint().finally(() => setLoading(false));
    } else if (params?.customerType === "pos") {
      fetchPosCustomerEndpoint().finally(() => setLoading(false));
    } else {
      fetchAppCustomerEndpoint().finally(() => setLoading(false));
    }
  }, []);

  // ── B2B ───────────────────────────────────────────────────────────────────

  async function fetchB2bCustomersEndpoint() {
    const results = await Promise.allSettled([
      getRetailUserDetails(params?.retailId),
      getCustomerOrderSummary(params?.retailId, { sourceId: params?.retailId }),
      getCustomerOrdersV1({ customerId: params?.retailId, organizationId: orgId }),
      customerAnalyticsOverviewApi(params?.retailId),
    ]);
    const [retailUserRes, orderSummaryRes, ordersRes, analyticsRes] = results;
    const retail =
      retailUserRes?.status === "fulfilled" ? retailUserRes?.value?.data?.data?.retail || {} : {};
    const orderSummary =
      orderSummaryRes?.status === "fulfilled" ? orderSummaryRes?.value?.data?.data || {} : {};
    const ordersData = ordersRes?.status === "fulfilled" ? ordersRes?.value?.data?.data || {} : {};
    const analyticsData =
      analyticsRes?.status === "fulfilled" ? analyticsRes?.value?.data?.customerOverview || {} : {};

    let uidx = null;
    const phoneNo = retail?.contacts?.[0]?.phoneNo || retail?.mobileNumber;
    if (phoneNo) {
      try {
        const b2cRes = await fetchB2BCustomerByPhoneNumber(phoneNo);
        uidx = b2cRes?.data?.customer?.uidx || null;
      } catch (err) {
        console.error(err);
      }
    }

    applyData({
      retail,
      orderSummary,
      ordersData,
      loyaltyData: {},
      walletData: {},
      couponList: [],
      tags: [retail?.city, retail?.retailType, "B2B"].filter(Boolean),
      uidx,
      analyticsData,
    });
  }

  // ── POS ───────────────────────────────────────────────────────────────────

  /*
  async function fetchPosCustomerEndpoint() {
    const firstResult = await Promise.allSettled([
      fetchCustomerLoyaltyPointDetails({
        customerId: params?.retailId,
        organizationId: orgId,
        locationId: locId,
      }),
      fetchRetailCustomer(params?.retailId, orgId),
      getCustomerOrdersV1({
        customerId: params?.retailId,
        locationId: locId,
        organizationId: orgId,
      }),
      getCustomerOrderSummary(params?.retailId, { sourceId: params?.retailId }),
    ]);
    const [loyaltyRes, retailCustomerRes, ordersRes, summaryRes] = firstResult;

    const customerDto =
      retailCustomerRes?.status === "fulfilled"
        ? retailCustomerRes?.value?.data?.customerDto || {}
        : {};
    const loyaltyData =
      loyaltyRes?.status === "fulfilled" ? loyaltyRes?.value?.data?.data || {} : {};
    const ordersData = ordersRes?.status === "fulfilled" ? ordersRes?.value?.data?.data || {} : {};
    const orderSummary =
      summaryRes?.status === "fulfilled" ? summaryRes?.value?.data?.data || {} : {};

    let couponList = [],
      b2cAddresses = [];

    if (customerDto?.mobileNumber) {
      const secondResult = await Promise.allSettled([
        fetchCoupounDetails({
          mobileNumber: customerDto.mobileNumber,
          organizationId: orgId,
          locationId: locId,
          page: 0,
          size: 10,
        }),
        fetchB2BCustomerByPhoneNumber(customerDto.mobileNumber),
      ]);
      const [couponRes, b2cCustomerRes] = secondResult;
      const couponData =
        couponRes?.status === "fulfilled" ? couponRes?.value?.data?.data || {} : {};
      const b2cCustomer = b2cCustomerRes?.status === "fulfilled" ? b2cCustomerRes?.value?.data?.customer : null;
      couponList =
        couponData?.statusCode === 200 ? couponData?.customerCouponDetailsDtoList || [] : [];
      b2cAddresses = b2cCustomer?.addresses || [];
      const uidx = params?.retailId || b2cCustomer?.uidx || null;
      applyData({
        retail: {
          ...customerDto,
          displayName: customerDto?.name,
          contacts: [{ phoneNo: customerDto?.mobileNumber, email: customerDto?.email || "" }],
          addresses: b2cAddresses,
        },
        orderSummary,
        ordersData,
        loyaltyData,
        walletData: {},
        couponList,
        tags: ["POS", "B2C"],
        uidx,
      });
    } else {
      applyData({
        retail: {
          ...customerDto,
          displayName: customerDto?.name,
          contacts: [{ phoneNo: customerDto?.mobileNumber, email: customerDto?.email || "" }],
          addresses: [],
        },
        orderSummary,
        ordersData,
        loyaltyData,
        walletData: {},
        couponList: [],
        tags: ["POS", "B2C"],
        uidx: params?.retailId || null,
      });
    }
  }
  */

  async function fetchPosCustomerEndpoint() {
    const custId = params?.customerId || params?.retailId;
    const firstResult = await Promise.allSettled([
      fetchCustomerLoyaltyPointDetails({
        customerId: custId,
        organizationId: orgId,
        locationId: locId,
      }),
      getCustomerDetailsV2(custId),
      getCustomerOrdersV1({
        customerId: custId,
        locationId: locId,
        organizationId: orgId,
      }),
      getCustomerOrderSummary(custId, { sourceId: custId }),
      customerAnalyticsOverviewApi(custId),
    ]);
    const [loyaltyRes, detailsRes, ordersRes, summaryRes, analyticsRes] = firstResult;

    const customer =
      detailsRes?.status === "fulfilled" ? detailsRes?.value?.data?.customer || {} : {};
    const loyaltyData =
      loyaltyRes?.status === "fulfilled" ? loyaltyRes?.value?.data?.data || {} : {};
    const ordersData = ordersRes?.status === "fulfilled" ? ordersRes?.value?.data?.data || {} : {};
    const orderSummary =
      summaryRes?.status === "fulfilled" ? summaryRes?.value?.data?.data || {} : {};
    const analyticsData =
      analyticsRes?.status === "fulfilled" ? analyticsRes?.value?.data?.customerOverview || {} : {};

    let couponList = [];
    const phoneNo = customer?.phoneNumber;
    if (phoneNo) {
      try {
        const couponRes = await fetchCoupounDetails({
          mobileNumber: phoneNo,
          organizationId: orgId,
          locationId: locId,
          page: 0,
          size: 10,
        });
        const couponData = couponRes?.data?.data || {};
        couponList =
          couponData?.statusCode === 200 ? couponData?.customerCouponDetailsDtoList || [] : [];
      } catch (err) {
        console.error(err);
      }
    }

    applyData({
      retail: {
        displayName: customer?.name || "N/A",
        contacts: [{ phoneNo: customer?.phoneNumber || "N/A", email: customer?.emailId || "N/A" }],
        addresses: customer?.addresses || [],
      },
      orderSummary,
      ordersData,
      loyaltyData,
      walletData: {},
      couponList,
      tags: ["POS", "B2C"],
      uidx: customer?.uidx || custId || null,
      analyticsData,
    });
  }

  // ── APP ───────────────────────────────────────────────────────────────────

  /*
  async function fetchAppCustomerEndpoint() {
    const firstResult = await Promise.allSettled([
      fetchB2BCustomerByPhoneNumber(params?.phoneNumber),
      fetchCoupounDetails({
        mobileNumber: params?.phoneNumber,
        organizationId: orgId,
        locationId: locId,
        page: 0,
        size: 10,
      }),
      getCustomerOrdersV1({ customerId: params?.customerId, organizationId: orgId }),
      getCustomerOrderSummary(params?.customerId, { sourceId: params?.customerId }),
      getWalletBalance(params?.customerId),
      fetchCustomerLoyaltyPointDetails({
        customerId: params?.customerId,
        organizationId: orgId,
        locationId: locId,
      }),
    ]);

    const [
      b2bCustomerDetailRes,
      couponDetailRes,
      customerOrderRes,
      customerOrderSummaryRes,
      walletBalanceRes,
      customerLoyaltyPointRes,
    ] = firstResult;

    const appCustomer =
      b2bCustomerDetailRes?.status === "fulfilled"
        ? b2bCustomerDetailRes?.value?.data?.customer || {}
        : {};

    let isCustomerExists = false;
    if (appCustomer?.uidx) {
      try {
        const res = await checkCustomerExist({
          uidx: appCustomer?.uidx,
          locationId: locId,
          organizationId: orgId,
        });
        isCustomerExists = res?.data?.isCustomerExists === true;
      } catch {
        showSnackbar("checkCustomerExist failed", "error");
      }
    }
    setCustomerExists(isCustomerExists);

    const couponData =
      couponDetailRes?.status === "fulfilled" ? couponDetailRes?.value?.data?.data || {} : {};
    const couponList =
      couponData?.statusCode === 200 ? couponData?.customerCouponDetailsDtoList || [] : [];
    const ordersData =
      customerOrderRes?.status === "fulfilled" ? customerOrderRes?.value?.data?.data || {} : {};
    const orderSummary =
      customerOrderSummaryRes?.status === "fulfilled"
        ? customerOrderSummaryRes?.value?.data?.data || {}
        : {};
    const walletData =
      walletBalanceRes?.status === "fulfilled" ? walletBalanceRes?.value?.data || {} : {};
    const loyaltyData =
      customerLoyaltyPointRes?.status === "fulfilled"
        ? customerLoyaltyPointRes?.value?.data?.data || {}
        : {};

    applyData({
      retail: {
        displayName: appCustomer?.name,
        contacts: [{ phoneNo: appCustomer?.phoneNumber, email: appCustomer?.emailId || "" }],
        addresses: appCustomer?.addresses || [],
      },
      orderSummary,
      ordersData,
      loyaltyData,
      walletData,
      couponList,
      tags: ["APP", "B2C"],
      uidx: appCustomer?.uidx || null,
    });
  }
  */

  async function fetchAppCustomerEndpoint() {
    const custId = params?.customerId || params?.retailId;
    const firstResult = await Promise.allSettled([
      getCustomerDetailsV2(custId),
      fetchCoupounDetails({
        mobileNumber: params?.phoneNumber || "",
        organizationId: orgId,
        locationId: locId,
        page: 0,
        size: 10,
      }),
      getCustomerOrdersV1({ customerId: custId, organizationId: orgId }),
      getCustomerOrderSummary(custId, { sourceId: custId }),
      getWalletBalance(custId),
      fetchCustomerLoyaltyPointDetails({
        customerId: custId,
        organizationId: orgId,
        locationId: locId,
      }),
      customerAnalyticsOverviewApi(custId),
    ]);

    const [
      detailsRes,
      couponDetailRes,
      customerOrderRes,
      customerOrderSummaryRes,
      walletBalanceRes,
      customerLoyaltyPointRes,
      analyticsRes,
    ] = firstResult;

    const customer =
      detailsRes?.status === "fulfilled" ? detailsRes?.value?.data?.customer || {} : {};

    let isCustomerExists = false;
    if (customer?.uidx) {
      try {
        const res = await checkCustomerExist({
          uidx: customer?.uidx,
          locationId: locId,
          organizationId: orgId,
        });
        isCustomerExists = res?.data?.isCustomerExists === true;
      } catch {
        showSnackbar("checkCustomerExist failed", "error");
      }
    }
    setCustomerExists(isCustomerExists);

    const couponData =
      couponDetailRes?.status === "fulfilled" ? couponDetailRes?.value?.data?.data || {} : {};
    const couponList =
      couponData?.statusCode === 200 ? couponData?.customerCouponDetailsDtoList || [] : [];
    const ordersData =
      customerOrderRes?.status === "fulfilled" ? customerOrderRes?.value?.data?.data || {} : {};
    const orderSummary =
      customerOrderSummaryRes?.status === "fulfilled"
        ? customerOrderSummaryRes?.value?.data?.data || {}
        : {};
    const walletData =
      walletBalanceRes?.status === "fulfilled" ? walletBalanceRes?.value?.data || {} : {};
    const loyaltyData =
      customerLoyaltyPointRes?.status === "fulfilled"
        ? customerLoyaltyPointRes?.value?.data?.data || {}
        : {};
    const analyticsData =
      analyticsRes?.status === "fulfilled" ? analyticsRes?.value?.data?.customerOverview || {} : {};

    applyData({
      retail: {
        displayName: customer?.name || "N/A",
        contacts: [{ phoneNo: customer?.phoneNumber || "N/A", email: customer?.emailId || "N/A" }],
        addresses: customer?.addresses || [],
      },
      orderSummary,
      ordersData,
      loyaltyData,
      walletData,
      couponList,
      tags: ["APP", "B2C"],
      uidx: customer?.uidx || custId || null,
      analyticsData,
    });
  }

  // ── Apply to state ────────────────────────────────────────────────────────

  function applyData({
    retail,
    orderSummary,
    ordersData,
    loyaltyData,
    walletData,
    couponList,
    tags,
    uidx,
    analyticsData,
  }) {
    setCustomerUidx(uidx || null);
    setMobileNumber(
      retail?.contacts?.[0]?.phoneNo || retail?.mobileNumber || params?.phoneNumber || ""
    );

    setProfile({
      name: retail?.displayName || "N/A",
      phone: retail?.contacts?.[0]?.phoneNo || retail?.mobileNumber || "N/A",
      email: retail?.contacts?.[0]?.email || "N/A",
      tags,
    });

    setSummary({
      outstandingPayables: orderSummary?.outStandingPayables ?? null,
      dueInDays: null,
      pendingDebitNote: null,
      unusedCredit: null,
      totalSales: analyticsData?.lifetimeValue ?? ordersData?.grandTotal ?? null,
      totalOrders: analyticsData?.lifetimeValueOrderCount ?? analyticsData?.totalSalesOrderCount ?? ordersData?.totalOrders ?? null,
      totalReturns: ordersData?.orderReturns ?? null,
      debitNotes: null,
      creditNotes: null,
      promoWallet: (analyticsData?.walletDetails?.find((w) => w?.type === "PROMO")?.balance) ?? walletData?.promoWalletAmount ?? null,
      promoWalletExpiry: null,
      userWallet: walletData?.defaultAmount ?? null,
      userWalletUpdated: null,
      loyaltyPoints: analyticsData?.loyaltyPoints ?? loyaltyData?.totalPoints ?? null,
      activeCoupons: couponList?.length || null,
    });

    setCustomerInfo({
      name: retail?.displayName || "N/A",
      phone: retail?.contacts?.[0]?.phoneNo || retail?.mobileNumber || "N/A",
      email: retail?.contacts?.[0]?.email || "N/A",
      country: retail?.addresses?.[0]?.country || "N/A",
      state: retail?.addresses?.[0]?.state || "N/A",
      city: retail?.addresses?.[0]?.city || "N/A",
      addressLine1: retail?.addresses?.[0]?.addressLine1 || "N/A",
      addressLine2: retail?.addresses?.[0]?.addressLine2 || "N/A",
    });

    const makeAddr = (a) =>
      a
        ? {
            line1: a?.addressLine1 || "N/A",
            city: a?.city || "N/A",
            state: a?.state || "N/A",
            country: a?.country || "N/A",
          }
        : null;

    const defaultAddr = retail?.addresses?.[0] || null;
    const billingAddr = retail?.addresses?.find((a) => a?.defaultBilling) || defaultAddr;
    const shippingAddr = retail?.addresses?.find((a) => a?.defaultShipping) || defaultAddr;

    setAddresses([
      { title: "Default Address", address: makeAddr(defaultAddr) },
      { title: "Billing Address", address: makeAddr(billingAddr) },
      { title: "Shipping Address", address: makeAddr(shippingAddr) },
    ]);

    setOverview({
      lifetimeValue: analyticsData?.lifetimeValue ?? ordersData?.grandTotal ?? null,
      totalDiscounts: ordersData?.totalDiscounts ?? null,
      customerRanking:
        ordersData?.customerRank != null && ordersData?.totalCustomers != null
          ? `${ordersData?.customerRank || 0} / ${ordersData?.totalCustomers || 0}`
          : null,
      totalOrders: analyticsData?.lifetimeValueOrderCount ?? analyticsData?.totalSalesOrderCount ?? ordersData?.totalOrders ?? null,
      averageCartValue: analyticsData?.averageCartValue ?? ordersData?.averageCartValue ?? null,
      lastSaleAmount: analyticsData?.lastSaleAmount ?? ordersData?.customerLastSaleValue ?? null,
      lastSaleDate: analyticsData?.lastSaleDate ?? ordersData?.createdDate ?? null,
      favoriteCategory: analyticsData?.favoriteCategory ?? null,
      visitGap: null,
    });
  }

  // ── Fetch Orders ──────────────────────────────────────────────────────────

  const fetchOrders = async (page = 0) => {
    setTabLoading(true);
    try {
      const res = await getAllOrders({
        page,
        pageSize: 10,
        destinationId: "",
        userId: params?.retailId || params?.customerId,
      });
      const data = res?.data?.data ?? {};
      setTabOrders(data?.orderResponse2List ?? []);
      setTabOrdersMeta({
        page: data?.page ?? page,
        pageSize: data?.pageSize ?? 10,
        totalResults: data?.totalResults ?? 0,
        totalPages: data?.totalPages ?? 0,
      });
    } catch {
      showSnackbar("Order fetch failed", "error");
      setTabOrders([]);
      setTabOrdersMeta({ page: 0, pageSize: 10, totalResults: 0, totalPages: 0 });
    } finally {
      setTabLoading(false);
    }
  };

  // ── Fetch Coupons ─────────────────────────────────────────────────────────

  const fetchCoupons = async (page = 0) => {
    setTabLoading(true);
    try {
      const res = await fetchCoupounDetails({
        mobileNumber: mobileNumber || "",
        organizationId: orgId,
        locationId: locId,
        page,
        size: 10,
      });
      const data = res?.data?.data ?? {};
      const list = data?.statusCode === 200 ? data?.customerCouponDetailsDtoList ?? [] : [];
      setTabCoupons(list);
      setTabCouponsMeta({
        page,
        pageSize: 10,
        totalResults: data?.numberOfRows ?? 0,
        totalPages: data?.numberOfPages ?? 0,
      });
    } catch {
      showSnackbar("Coupons fetch failed", "error");
      setTabCoupons([]);
      setTabCouponsMeta({ page: 0, pageSize: 10, totalResults: 0, totalPages: 0 });
    } finally {
      setTabLoading(false);
    }
  };

  // ── Tab click handler ─────────────────────────────────────────────────────

  const handleTabClick = async (tab) => {
    setActiveDetailTab(tab);
    if (tab === "Order") {
      fetchOrders(0);
    } else if (tab === "Loyalty") {
      setTabLoading(true);
      let loyaltyRes = null;
      try {
        loyaltyRes = await getCustomerLoyaltyDetail({
          customerId: params?.retailId || params?.customerId,
          organizationId: orgId,
          locationId: locId,
          page: 0,
          size: 10,
        });
        setTabLoyalty(loyaltyRes?.data?.data ?? {});
      } catch {
        showSnackbar("Loyalty fetch failed", "error");
        setTabLoyalty(null);
      }

      // Enrichment is best-effort: a failure here must NOT discard the primary
      // loyalty data fetched above.
      try {
        let uidxToUse =
          customerUidx || loyaltyRes?.data?.data?.customerLoyaltyDto?.customerUidx;
        if (!uidxToUse) {
          const phone = mobileNumber || profile?.phone;
          if (phone && phone !== "N/A") {
            const b2cRes = await fetchB2BCustomerByPhoneNumber(phone);
            uidxToUse = b2cRes?.data?.customer?.uidx;
            if (uidxToUse) {
              setCustomerUidx(uidxToUse);
            }
          }
        }

        if (uidxToUse) {
          const detailsRes = await getLoyaltyDetails(orgId, uidxToUse);
          if (detailsRes?.data?.data?.status) {
            setLoyaltyDetails(detailsRes?.data?.data?.data ?? null);
          }
        }
      } catch {
        setLoyaltyDetails(null);
      } finally {
        setTabLoading(false);
      }
    } else if (tab === "Wallet") {
      setTabLoading(true);
      try {
        const res = await getWalletBalance(params?.retailId || params?.customerId);
        setTabWallet(res?.data ?? {});
      } catch {
        showSnackbar("Wallet fetch failed", "error");
        setTabWallet(null);
      } finally {
        setTabLoading(false);
      }
    } else if (tab === "Coupons") {
      fetchCoupons(0);
    }
  };

  // ── Tab renderer ──────────────────────────────────────────────────────────

  const renderTabContent = () => {
    switch (activeDetailTab) {
      case "Overview":
        return <OverviewTab overview={overview} />;

      case "Order":
        return (
          <CustomerPaginatedTable
            columns={ORDER_COLUMNS}
            rows={mapOrderRows(tabOrders)}
            loading={tabLoading}
            meta={tabOrdersMeta}
            onPageChange={fetchOrders}
            emptyText="No orders found."
            rowKey="orderId"
          />
        );

      case "Loyalty":
        return (
          <LoyaltyTab
            loyalty={tabLoyalty}
            loading={tabLoading}
            summary={summary}
            loyaltyDetails={loyaltyDetails}
          />
        );

      case "Wallet":
        return <WalletTab wallet={tabWallet} loading={tabLoading} />;

      case "Coupons":
        return (
          <CustomerPaginatedTable
            columns={COUPON_COLUMNS}
            rows={mapCouponRows(tabCoupons)}
            loading={tabLoading}
            meta={tabCouponsMeta}
            onPageChange={fetchCoupons}
            emptyText="No coupons found."
            rowKey="code"
          />
        );

      default:
        return null;
    }
  };

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <Navbar>
      <main className="cpv__main">
        {loading ? (
          <div className="cpv__full-loader">
            <div className="cpv__spinner" />
          </div>
        ) : (
          <div className="cpv__grid-root">
            {/* HEADER */}
            <section className="cpv__header-card cpv__area-header">
              <div className="cpv__header-left">
                <div className="cpv__go-back" onClick={() => navigate(-1)}>
                  <PosCustomIcon
                    icon={<ChevronLeftIcon />}
                    height="16px"
                    width="16px"
                    color="black"
                    // onClickFunction={() => navigate(-1)}
                  />
                </div>
                <div className="cpv__avatar">{getInitials(profile?.name)}</div>
                <div className="cpv__header-meta">
                  <h2 className="cpv__customer-name">{profile?.name || "N/A"}</h2>
                  <div className="cpv__contact-row">
                    {profile?.phone && (
                      <span className="cpv__contact-item">
                        <PosCustomIcon
                          icon={<PhoneIcon />}
                          height="14px"
                          width="14px"
                          color="#94a3b8"
                        />
                        {profile.phone}
                      </span>
                    )}
                    {profile?.email && (
                      <span className="cpv__contact-item">
                        <PosCustomIcon
                          icon={<EnvelopeIcon />}
                          width="14px"
                          height="14px"
                          color="#94a3b8"
                        />
                        {profile?.email}
                      </span>
                    )}
                  </div>
                  <div className="cpv__tags">
                    {profile?.tags?.map((tag) => (
                      <span key={tag} className="cpv__tag">
                        {tag}
                      </span>
                    ))}
                    {params?.customerType !== "b2b" &&
                      params?.customerType !== "pos" &&
                      !customerExists && (
                        <span className="cpv__tag cpv__tag--warning">Not Registered in POS</span>
                      )}
                  </div>
                </div>
              </div>

              <div className="cpv__outstanding">
                <span className="cpv__outstanding-label">OUTSTANDING PAYABLES</span>
                <span className="cpv__outstanding-value">
                  {formatCurrency(summary?.outstandingPayables)}
                </span>
                <span className="cpv__outstanding-sub">
                  {summary?.outstandingPayables === 0
                    ? "No pending dues"
                    : summary?.dueInDays
                    ? `Due in ${summary.dueInDays} days`
                    : ""}
                </span>
                <div className="cpv__payable-notes">
                  <span className="cpv__note cpv__note--orange">
                    ● {formatCurrency(summary?.pendingDebitNote)} pending debit note
                  </span>
                  <span className="cpv__note cpv__note--green">
                    ● {formatCurrency(summary?.unusedCredit)} unused credit
                  </span>
                </div>
              </div>
            </section>

            {/* STAT CARDS */}
            <section className="cpv__stats-row cpv__area-stats">
              <StatCard
                label="TOTAL SALES"
                value={formatCurrency(summary?.totalSales)}
                sub={`from ${summary?.totalOrders ?? "N/A"} orders`}
              />
              <StatCard
                label="TOTAL RETURNS"
                value={formatCurrency(summary?.totalReturns)}
                sub="from returns"
              />
              <StatCard
                label="DEBIT NOTES"
                value={formatCurrency(summary?.debitNotes)}
                sub="open"
              />
              <StatCard
                label="CREDIT NOTES"
                value={formatCurrency(summary?.creditNotes)}
                sub="from returns"
              />
              <StatCard
                label="PROMO WALLET"
                value={formatCurrency(summary?.promoWallet)}
                sub={
                  summary?.promoWalletExpiry
                    ? `expires ${formatDate(summary?.promoWalletExpiry)}`
                    : ""
                }
              />
              <StatCard
                label="USER WALLET"
                value={summary?.userWallet != null ? formatCurrency(summary?.userWallet) : "N/A"}
                sub={summary?.userWalletUpdated ? `Updated ${summary?.userWalletUpdated}` : ""}
              />
              <StatCard
                label="LOYALTY POINTS"
                value={summary?.loyaltyPoints ?? "N/A"}
                sub="from reward points"
              />
              <StatCard
                label="ACTIVE COUPONS"
                value={summary?.activeCoupons ?? "N/A"}
                sub="available now"
              />
            </section>

            {/* CUSTOMER INFO */}
            <section className="cpv__card cpv__area-info">
              <h3 className="cpv__section-title">CUSTOMER INFORMATION</h3>
              <InfoRow label="Customer Name" value={customerInfo?.name} />
              <InfoRow label="Contact number" value={customerInfo?.phone} />
              <InfoRow label="Email" value={customerInfo?.email} />
              <InfoRow label="Country" value={customerInfo?.country} />
              <InfoRow label="State" value={customerInfo?.state} />
              <InfoRow label="City" value={customerInfo?.city} />
              <InfoRow
                label="Address"
                value={
                  customerInfo?.addressLine1 && customerInfo?.addressLine1 !== "N/A"
                    ? [customerInfo?.addressLine1, customerInfo?.addressLine2]
                        .filter((v) => v && v !== "N/A")
                        .join(", ")
                    : "N/A"
                }
              />
              {customerInfo?.addressLine1 && customerInfo?.addressLine1 !== "N/A" && (
                <a
                  className="cpv__map-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [
                      customerInfo?.addressLine1,
                      customerInfo?.city,
                      customerInfo?.state,
                      customerInfo?.country,
                    ]
                      .filter((v) => v && v !== "N/A")
                      .join(", ")
                  )}`}
                >
                  <PosCustomIcon icon={<MapPinIcon />} height="14px" width="14px" color="#3b6ef6" />
                  View in Map
                </a>
              )}
            </section>

            {/* DETAIL TABS */}
            <section className="cpv__area-detail">
              <div className="cpv__pill-tabs">
                {DETAIL_TABS?.map((tab) => (
                  <button
                    key={tab}
                    className={`cpv__pill-tab ${activeDetailTab === tab ? "active" : ""}`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div
                className={
                  ["Loyalty", "Wallet"].includes(activeDetailTab)
                    ? "cpv__tab-panel-with-background"
                    : "cpv__tab-panel"
                }
              >
                {renderTabContent()}
              </div>
            </section>

            {/* BILLING */}
            <section className="cpv__card cpv__area-billing">
              <h3 className="cpv__section-title">BILLING DETAILS</h3>
              {addresses?.map((a) => (
                <AddressAccordion key={a?.title} title={a?.title} address={a?.address} />
              ))}
            </section>
          </div>
        )}
      </main>
    </Navbar>
  );
};

export default CustomerV2Preview;
